import { useState } from "react";
import { useMarketplaceConfig } from "./useMarketplace";
import { useSession } from "./useSession";
import { SellLandWizard, type SellSourceParcel } from "./SellLandWizard";
import { useT } from "../../i18n";

/**
 * Entry B — the REAL "Sell Land" affordance on the canonical Grid's Parcel
 * Profile. Shown to the authenticated OWNER of the selected parcel whenever the
 * marketplace is really enabled. It is INDEPENDENT of PREVIEW_MARKETPLACE_DEMO
 * (the demo flag only governs the isolated Preview Demo Grid / demo chrome,
 * never whether a legitimate owner can sell) and opens the SAME shared seller
 * wizard used by MY MARKET → LIST LAND (Entry A) — no duplicate flow.
 */
export function SellLandGridEntry({ parcel }: { parcel: SellSourceParcel }) {
  const t = useT();
  const { data: config } = useMarketplaceConfig();
  const session = useSession();
  const [open, setOpen] = useState(false);

  const ready = !!config?.enabled && !!config?.marketplace_enabled;
  const isOwner =
    !!session.wallet &&
    session.wallet.toLowerCase() === String(parcel.owner || "").toLowerCase();

  // Real gating: authenticated wallet + actual ownership + marketplace readiness.
  if (!ready || !session.isAuthenticated || !isOwner) return null;

  return (
    <div className="flex flex-col gap-1.5" data-testid="grid-sell-land-entry">
      <button
        type="button"
        data-testid="grid-sell-land"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md font-mono text-[10.5px] font-semibold tracking-[0.16em] uppercase transition-[background-color,box-shadow] bg-neon-coral/15 text-neon-coral border border-border-coral hover:bg-neon-coral/25 hover:shadow-glow-coral-sm"
      >
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M7 1l4.5 1.8v3.4c0 2.9-1.9 4.9-4.5 5.8-2.6-.9-4.5-2.9-4.5-5.8V2.8L7 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
        </svg>
        {t("marketplace.sell.sellLand")}
      </button>
      <SellLandWizard
        key={`${parcel.parcel_id}:${open}`}
        open={open}
        sourceParcel={open ? parcel : null}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
